/**
 * useAnalyticsHelpers Hook
 * 
 * Provides helper functions for the Analytics page:
 * - Currency formatting and conversion
 * - Account name resolution
 * - Date range calculations
 * - User name resolution
 * - Category color assignment
 */

import { useMemo, useState, useEffect } from "react";
import { Currency, Account } from "../../../types";
import { auth, db } from "../../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { WorkspaceMember } from "./useAnalyticsWorkspaceMembers";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";
import { isDemoMode } from "../../../utils/demoMode";
import { getCurrencyDecimalPlaces, getDefaultDecimalPlaces } from "../../../utils/currencyDefaults";
import { formatCurrency as formatCurrencyUtil } from "../../../utils/transactionUtils";
import { getCurrencyByCode } from "../../../utils/iso4217Currencies";

// Optimized color palette - size-based progression
const COLOR_PALETTE = [
  "#EF4444", // Light Red (largest segments - not too dark, a little lighter)
  "#F97316", // Orange (second largest percentages)
  "#F59E0B", // Amber (orange to yellow transition)
  "#EAB308", // Yellow
  "#16A34A", // Dark Green
  "#22C55E", // Light Green
  "#2563EB", // Blue (distinct from red/orange/yellow/green)
  "#7C3AED", // Violet (distinct purple)
  "#06B6D4", // Cyan (distinct blue-green)
  "#8B5CF6", // Purple (distinct from violet)
  "#10B981", // Emerald (distinct green variant)
  "#EC4899", // Pink (distinct from red)
  "#14B8A6", // Teal (distinct blue-green)
  "#C026D3", // Fuchsia (distinct pink-purple)
  "#84CC16", // Lime (distinct yellow-green)
  "#A855F7", // Light Purple (distinct variant)
  "#5290FF", // Indigo (distinct blue-purple)
  "#DC2626", // Deep Red (darker red variant for smaller segments)
];

/**
 * Get a distinct color for a category by index
 * Uses modulo to cycle through the palette for categories beyond palette length
 * This ensures each category gets a unique, visible color
 */
export const getCategoryColor = (index: number): string => {
  return COLOR_PALETTE[index % COLOR_PALETTE.length];
};

export interface UseAnalyticsHelpersParams {
  currencies: Currency[];
  accounts: Account[];
  workspaceMembers: WorkspaceMember[];
  defaultCurrency: string;
  workspaceCreatedAt: Date | null;
  transactions?: Array<{ userId?: string }>; // Optional: to fetch user names for users not in workspaceMembers
}

export interface UseAnalyticsHelpersReturn {
  // Currency helpers
  getCurrencySymbol: () => string;
  formatAmount: (amount: number) => string;
  formatCurrency: (amount: number, currencyCode: string) => string;
  getCurrencyRate: (currencyCode: string) => number;
  convertToDefaultCurrency: (
    amount: number, 
    currencyCode: string | undefined,
    amountInDefaultCurrency?: number,
    defaultCurrencyAtTime?: string
  ) => number;

  // Account helpers
  getAccountName: (accountId: string | undefined, storedAccountName?: string) => string;
  getAccountDisplayName: (accountId: string) => string;

  // Date helpers
  setDateRangeByPeriod: (periodType: string, offset: number, setFromDate: (date: string) => void, setToDate: (date: string) => void) => void;

  // User helpers
  getUserName: (userId: string) => string;
}

/**
 * Hook for Analytics helper functions
 */
export const useAnalyticsHelpers = ({
  currencies,
  accounts,
  workspaceMembers,
  defaultCurrency,
  workspaceCreatedAt,
  transactions = [],
}: UseAnalyticsHelpersParams): UseAnalyticsHelpersReturn => {
  // Get currency symbol for default currency
  const getCurrencySymbol = (): string => {
    // First, try to find currency in the currencies array (from database)
    const currency = currencies.find((c) => c.code === defaultCurrency);
    if (currency?.symbol) {
      return currency.symbol;
    }
    
    // If currency not found in array yet (still loading), use fallback from ISO4217
    // This prevents showing empty symbol when defaultCurrency changes but currencies haven't updated
    if (defaultCurrency) {
      const fallbackCurrency = getCurrencyByCode(defaultCurrency);
      if (fallbackCurrency?.symbol) {
        return fallbackCurrency.symbol;
      }
    }
    
    // If no symbol found, return empty string (don't show wrong symbol)
    return "";
  };

  // Format amount with currency symbol (for default currency)
  const formatAmount = (amount: number): string => {
    // Safety check: if defaultCurrency is not loaded yet, return empty string
    if (!defaultCurrency || defaultCurrency === "") {
      return "";
    }
    // Get decimal places for default currency
    const decimalPlaces = getCurrencyDecimalPlaces(defaultCurrency, currencies, defaultCurrency);
    // Use the formatCurrency utility which respects decimal places
    return formatCurrencyUtil(amount, defaultCurrency, decimalPlaces);
  };

  // Format currency amount with currency code
  const formatCurrency = (amount: number, currencyCode: string): string => {
    // Get decimal places for this currency
    const decimalPlaces = getCurrencyDecimalPlaces(currencyCode, currencies, defaultCurrency);
    // Use the formatCurrency utility which respects decimal places
    return formatCurrencyUtil(amount, currencyCode, decimalPlaces);
  };

  // Get account name by ID
  const getAccountName = (accountId: string | undefined, storedAccountName?: string): string => {
    if (!accountId) {
      return storedAccountName ? `${storedAccountName} (Deleted)` : "Unknown Account";
    }
    const account = accounts.find((acc) => acc.id === accountId);
    if (account) {
      return account.accountName;
    }
    // Account not found, use stored name if available
    return storedAccountName ? `${storedAccountName} (Deleted)` : "Unknown Account";
  };

  // Get account display name for filter button (with balance info)
  const getAccountDisplayName = (accountId: string): string => {
    const account = accounts.find((acc) => acc.id === accountId);
    if (account) {
      return `${account.accountName} (${formatCurrency(account.amount || 0, account.currency)})`;
    }
    return "Unknown Account";
  };

  // Get currency rate (rate relative to default currency)
  const getCurrencyRate = (currencyCode: string): number => {
    if (currencyCode === defaultCurrency) return 1;
    const currency = currencies.find((c) => c.code === currencyCode);
    return currency?.rate || 1;
  };

  // Convert amount to default currency
  // Prefer using amountInDefaultCurrency from transaction when valid; otherwise convert with current rates.
  // Rate represents: 1 defaultCurrency = rate currency. To convert FROM currency TO default: divide by rate.
  const convertToDefaultCurrency = (
    amount: number,
    currencyCode: string | undefined,
    amountInDefaultCurrency?: number,
    defaultCurrencyAtTime?: string
  ): number => {
    if (!defaultCurrency || defaultCurrency === "") return 0;

    const isSameCurrency = !currencyCode || currencyCode === defaultCurrency;

    // Use stored amount when: we have it, and (default currency unchanged, or legacy without defaultCurrencyAtTime)
    if (amountInDefaultCurrency !== undefined && amountInDefaultCurrency !== null) {
      const defaultUnchanged = defaultCurrencyAtTime == null || defaultCurrencyAtTime === defaultCurrency;
      if (defaultUnchanged) {
        // Same-currency sanity: if transaction is in default currency, stored value should be close to amount
        if (isSameCurrency && amount > 0) {
          const ratio = amountInDefaultCurrency / amount;
          if (ratio >= 0.1 && ratio <= 10) return amountInDefaultCurrency;
          return amount;
        }
        return amountInDefaultCurrency;
      }
    }

    if (isSameCurrency) return amount;
    const rate = getCurrencyRate(currencyCode);
    return amount / rate;
  };

  // Set date range based on period type and offset
  const setDateRangeByPeriod = (
    periodType: string,
    offset: number = 0,
    setFromDate: (date: string) => void,
    setToDate: (date: string) => void
  ): void => {
    const today = new Date();
    let from: Date;
    let to: Date;

    switch (periodType) {
      case "All time": {
        // Use workspace creation date if available, otherwise use a very old date
        from = workspaceCreatedAt || new Date(2000, 0, 1);
        to = today;
        break;
      }
      case "Daily": {
        const targetDate = new Date(today);
        targetDate.setDate(today.getDate() + offset);
        from = new Date(targetDate);
        to = new Date(targetDate);
        break;
      }
      case "Weekly": {
        const targetDate = new Date(today);
        targetDate.setDate(today.getDate() + offset * 7);
        const dayOfWeek = targetDate.getDay();
        from = new Date(targetDate);
        from.setDate(targetDate.getDate() - dayOfWeek); // Start of week (Sunday)
        to = new Date(from);
        to.setDate(from.getDate() + 6); // End of week (Saturday)
        break;
      }
      case "Monthly": {
        const targetDate = new Date(today);
        targetDate.setMonth(today.getMonth() + offset);
        from = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
        to = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
        break;
      }
      case "Yearly": {
        const targetDate = new Date(today);
        targetDate.setFullYear(today.getFullYear() + offset);
        from = new Date(targetDate.getFullYear(), 0, 1);
        to = new Date(targetDate.getFullYear(), 11, 31);
        break;
      }
      default:
        return;
    }

    // Use local date strings to avoid timezone issues (YYYY-MM-DD format)
    const formatLocalDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    setFromDate(formatLocalDate(from));
    setToDate(formatLocalDate(to));
  };

  // Cache for user names fetched from users collection (for users not in workspaceMembers)
  const [userNameCache, setUserNameCache] = useState<Record<string, string>>({});
  
  // Pre-fetch user names from users collection for users in transactions but not in workspaceMembers
  useEffect(() => {
    if (transactions.length === 0) return;
    
    // Get all unique user IDs from transactions
    const transactionUserIds = new Set(
      transactions
        .map(t => t.userId)
        .filter((userId): userId is string => Boolean(userId))
    );
    
    // Find user IDs that are in transactions but not in workspaceMembers
    const workspaceMemberIds = new Set(workspaceMembers.map(u => u.id));
    const userIdsToFetch = Array.from(transactionUserIds).filter(
      userId => !workspaceMemberIds.has(userId)
    );
    
    if (userIdsToFetch.length === 0) return;
    
    // Fetch user documents for these IDs (only if not already in cache)
    const userIdsToFetchNow = userIdsToFetch.filter(userId => !userNameCache[userId]);
    if (userIdsToFetchNow.length === 0) return;
    
    // Fetch user documents for these IDs
    const fetchPromises = userIdsToFetchNow.map(async (userId) => {
      try {
        const userDoc = await getDoc(doc(db, "users", userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const fetchedName = (userData.name as string | undefined) || 
                             (userData.displayName as string | undefined) || 
                             (userData.email as string | undefined);
          
          if (fetchedName) {
            // Format if it's an email
            let formattedName = fetchedName;
            if (fetchedName.includes("@")) {
              const emailPrefix = fetchedName.split("@")[0];
              formattedName = emailPrefix
                .split(/[._-]/)
                .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
                .join(" ");
            }
            return { userId, name: formattedName };
          }
        }
      } catch (error) {
        // Silently fail - user document might not exist or be accessible
      }
      return null;
    });
    
    Promise.all(fetchPromises).then((results) => {
      const updates: Record<string, string> = {};
      results.forEach((result) => {
        if (result) {
          updates[result.userId] = result.name;
        }
      });
      if (Object.keys(updates).length > 0) {
        setUserNameCache(prev => ({ ...prev, ...updates }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, workspaceMembers]);

  // Get user name by ID - matches Activity page behavior exactly
  // Prioritizes proper names over emails, formats email addresses if they appear in name field
  // Also tries to fetch from users collection if user isn't in workspaceMembers
  const getUserName = (userId: string): string => {
    // First, check if user exists in workspaceMembers
    const user = workspaceMembers.find((u) => u.id === userId);
    if (user) {
      // Use name, email, or fallback to userId prefix - avoid "Unknown User" when possible (matches Activity)
      let displayName = user.name || user.email;
      
      // If name is an email address (contains @), format it nicely
      // Example: "zargarianvahagn@gmail.com" → "Zargarianvahagn"
      if (displayName && displayName.includes("@")) {
        const emailPrefix = displayName.split("@")[0];
        displayName = emailPrefix
          .split(/[._-]/)
          .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
          .join(" ");
      }
      
      // If still no name/email, use userId prefix as last resort (matches Activity)
      // In demo mode, use "Demo User" instead
      if (!displayName) {
        displayName = isDemoMode() ? "Demo User" : `User ${userId.substring(0, 8)}`;
      }
      
      // Check if there are duplicate names (number them if needed)
      const usersWithSameName = workspaceMembers.filter(
        (u) => {
          let otherName = u.name || u.email;
          if (otherName && otherName.includes("@")) {
            const emailPrefix = otherName.split("@")[0];
            otherName = emailPrefix
              .split(/[._-]/)
              .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
              .join(" ");
          }
          if (!otherName) {
            otherName = isDemoMode() ? "Demo User" : `User ${u.id.substring(0, 8)}`;
          }
          return otherName === displayName && u.id !== userId;
        }
      );
      
      // If there are duplicate names, number them
      if (usersWithSameName.length > 0) {
        const allUsersWithSameName = workspaceMembers.filter(
          (u) => {
            let otherName = u.name || u.email;
            if (otherName && otherName.includes("@")) {
              const emailPrefix = otherName.split("@")[0];
              otherName = emailPrefix
                .split(/[._-]/)
                .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
                .join(" ");
            }
            if (!otherName) {
              otherName = isDemoMode() ? "Demo User" : `User ${u.id.substring(0, 8)}`;
            }
            return otherName === displayName;
          }
        );
        const sortedUsers = [...allUsersWithSameName].sort((a, b) => a.id.localeCompare(b.id));
        const userIndex = sortedUsers.findIndex((u) => u.id === userId);
        const userNumber = userIndex + 1;
        displayName = `${displayName} (${userNumber})`;
      }
      
      return displayName;
    }
    
    // In bypass auth mode, if userId is not found, check if it might be an old workspaceOwnerId
    // In that case, try to find "bypass-user" member and use its name
    if (isBypassAuthEnabled()) {
      const bypassUser = workspaceMembers.find((u) => u.id === "bypass-user");
      if (bypassUser) {
        // Use bypass-user's name if available
        let displayName = bypassUser.name || bypassUser.email;
        
        // If name is an email address (contains @), format it nicely
        if (displayName && displayName.includes("@")) {
          const emailPrefix = displayName.split("@")[0];
          displayName = emailPrefix
            .split(/[._-]/)
            .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join(" ");
        }
        
        if (displayName) {
          return displayName;
        }
      }
    }
    
    // Check cache first (for users we've already fetched)
    if (userNameCache[userId]) {
      return userNameCache[userId];
    }
    
    // Bypass-user ID - special handling (matches Activity)
    const BYPASS_USER_ID = "bypass-user";
    
    // Special case for bypass-user
    if (userId === BYPASS_USER_ID) {
      return "Bypass-User";
    }
    
    // Fallback: use auth user if it's the current user
    if (auth.currentUser && auth.currentUser.uid === userId) {
      const authName = auth.currentUser.displayName || auth.currentUser.email || `User ${userId.substring(0, 8)}`;
      return authName;
    }
    
    // In demo mode, show "Demo User" instead of user IDs
    if (isDemoMode()) {
      return "Demo User";
    }
    
    // Last resort: use userId prefix (matches Activity)
    // Note: If user name is being fetched, it will be cached and component will re-render
    return `User ${userId.substring(0, 8)}`;
  };

  return {
    getCurrencySymbol,
    formatAmount,
    formatCurrency,
    getCurrencyRate,
    convertToDefaultCurrency,
    getAccountName,
    getAccountDisplayName,
    setDateRangeByPeriod,
    getUserName,
  };
};
